import React, { useState } from 'react';
import {
  Stack,
  Box,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Link,
  Alert
} from '@mui/material';
import { Refresh as RefreshIcon, Delete as DeleteIcon } from '@mui/icons-material';
import VideoLinkSection from './VideoLinkSection';
import { useFeatureFlag } from '../FeatureFlags/useFeatureFlags';
import RecruitApiClient from '../RecruitApiClient';

interface VideoConferenceManagerProps {
  eventId: number;
  hasVideoLink: boolean;
  videoUrl?: string;
  videoProvider?: string;
  onVideoConferenceUpdate: (data: {
    video_conference_url?: string;
    video_conference_provider?: string;
  }) => void;
  onEventUpdate?: (updatedEvent: any) => void;
  isEditable?: boolean;
}

export default function VideoConferenceManager({
  eventId,
  hasVideoLink,
  videoUrl,
  videoProvider,
  onVideoConferenceUpdate,
  onEventUpdate,
  isEditable = true
}: VideoConferenceManagerProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [hasVideoConferenceIntegration, setHasVideoConferenceIntegration] = useState(false);
  const [provider, setProvider] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [integrationExpired, setIntegrationExpired] = useState(false);
  const [daysUntilExpiry, setDaysUntilExpiry] = useState<number | null>(null);
  const hasVideoConferencePermission = useFeatureFlag('videoConferenceIntegrationEnabled');

  React.useEffect(() => {
    RecruitApiClient.get<{
      has_integration: boolean;
      provider?: string;
      expired?: boolean;
      days_until_expiry?: number;
    }>('/api/v4/video_conferences/status')
      .then(({ res }) => {
        setHasVideoConferenceIntegration(res.has_integration);
        setProvider(res.provider || null);
        setIntegrationExpired(res.expired || false);
        setDaysUntilExpiry(res.days_until_expiry || null);
      })
      .catch(() => {
        setHasVideoConferenceIntegration(false);
        setIntegrationExpired(false);
        setDaysUntilExpiry(null);
      });
  }, []);

  const getVideoConferenceLabel = (provider: string | null): string => {
    switch (provider) {
      case 'zoom':
        return 'Zoom Meeting';
      case 'google_meet':
        return 'Google Meeting';
      case 'teams':
        return 'Teams Meeting';
      default:
        return 'Video Conferencing';
    }
  };

  const handleOAuthReconnection = (reconnectionUrl: string) => {
    setErrorMessage(
      'Your video conferencing integration has expired. Opening reconnection window...'
    );

    // Open OAuth in a popup window
    const popup = window.open(
      reconnectionUrl,
      'oauth_reconnection',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    if (!popup) {
      // Popup blocked, fall back to redirect
      setErrorMessage('Popup blocked. Redirecting to reconnection page...');
      setTimeout(() => {
        window.location.href = reconnectionUrl;
      }, 2000);
      return;
    }

    // Monitor popup for completion
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        setErrorMessage('Reconnection completed. Please try again.');
        // Refresh the integration status
        window.location.reload();
      }
    }, 1000);

    // Timeout after 5 minutes
    setTimeout(() => {
      clearInterval(checkClosed);
      if (!popup.closed) {
        popup.close();
        setErrorMessage('Reconnection timed out. Please try again or visit admin settings.');
      }
    }, 300000);
  };

  const handleExpiredIntegrationReconnection = async () => {
    if (!provider) return;

    setIsLoading(true);
    setErrorMessage('Initiating reconnection for expired integration...');

    try {
      // Call the backend to initiate reconnection
      const response = await fetch(`/api/v4/video_conferences/initiate_reconnection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: provider })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.reconnection_url) {
          handleOAuthReconnection(data.reconnection_url);
        } else {
          setErrorMessage('Failed to generate reconnection URL. Please try again.');
        }
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.errors || 'Failed to initiate reconnection. Please try again.');
      }
    } catch (error) {
      setErrorMessage(
        'Failed to initiate reconnection. Please check your connection and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnableVideoConference = async () => {
    if (!hasVideoConferenceIntegration) return;

    setIsLoading(true);
    setErrorMessage(null);
    try {
      const response = await fetch(`/api/v4/calendar/events/${eventId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: {
            enable_video_conferencing: true
          }
        })
      });

      if (response.ok) {
        const updatedEvent = await response.json();
        onVideoConferenceUpdate({
          video_conference_url: updatedEvent.video_conference_url,
          video_conference_provider: updatedEvent.video_conference_provider
        });
        onEventUpdate?.(updatedEvent);
      } else {
        const errorData = await response.json();

        // Check if this is an OAuth reconnection error
        if (errorData.error_type === 'oauth_reconnection_required' && errorData.reconnection_data) {
          handleOAuthReconnection(errorData.reconnection_data.reconnection_url);
          return;
        }

        setErrorMessage(
          errorData.errors || 'Failed to enable video conferencing. Please try again.'
        );
      }
    } catch (error) {
      setErrorMessage(
        'Failed to enable video conferencing. Please check your connection and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableVideoConference = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const response = await fetch(`/api/v4/calendar/events/${eventId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: {
            enable_video_conferencing: false
          }
        })
      });

      if (response.ok) {
        const updatedEvent = await response.json();
        onVideoConferenceUpdate({
          video_conference_url: undefined,
          video_conference_provider: undefined
        });
        onEventUpdate?.(updatedEvent);
      } else {
        const errorData = await response.json();

        // Check if this is an OAuth reconnection error
        if (errorData.error_type === 'oauth_reconnection_required' && errorData.reconnection_data) {
          handleOAuthReconnection(errorData.reconnection_data.reconnection_url);
          return;
        }

        setErrorMessage(
          errorData.errors || 'Failed to remove video conferencing. Please try again.'
        );
      }
    } catch (error) {
      setErrorMessage(
        'Failed to remove video conferencing. Please check your connection and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshVideoLink = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const response = await fetch(`/api/v4/calendar/events/${eventId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: {
            enable_video_conferencing: true
          }
        })
      });

      if (response.ok) {
        const updatedEvent = await response.json();
        onVideoConferenceUpdate({
          video_conference_url: updatedEvent.video_conference_url,
          video_conference_provider: updatedEvent.video_conference_provider
        });
        onEventUpdate?.(updatedEvent);
      } else {
        const errorData = await response.json();

        // Check if this is an OAuth reconnection error
        if (errorData.error_type === 'oauth_reconnection_required' && errorData.reconnection_data) {
          handleOAuthReconnection(errorData.reconnection_data.reconnection_url);
          return;
        }

        setErrorMessage(errorData.errors || 'Failed to refresh video link. Please try again.');
      }
    } catch (error) {
      setErrorMessage('Failed to refresh video link. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasVideoConferencePermission) {
    return null;
  }

  // Read-only mode - just show the video link if it exists
  if (!isEditable) {
    return hasVideoLink ? (
      <VideoLinkSection videoUrl={videoUrl || ''} videoProvider={videoProvider} />
    ) : null;
  }

  return (
    <Stack sx={{ gap: '16px' }}>
      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          sx={{
            fontSize: '0.875rem',
            '& .MuiAlert-message': {
              padding: '0'
            }
          }}
        >
          {errorMessage}
        </Alert>
      )}
      {hasVideoLink ? (
        <>
          <VideoLinkSection videoUrl={videoUrl || ''} videoProvider={videoProvider} />
          <Stack sx={{ flexDirection: 'row', gap: '12px', alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefreshVideoLink}
              disabled={isLoading || !hasVideoConferenceIntegration}
              startIcon={<RefreshIcon />}
              sx={{
                textTransform: 'none',
                borderColor: '#5BC4BF',
                color: '#5BC4BF',
                '&:hover': {
                  borderColor: '#4A9B96',
                  color: '#4A9B96'
                }
              }}
            >
              Refresh Link
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDisableVideoConference}
              disabled={isLoading}
              startIcon={<DeleteIcon />}
              sx={{
                textTransform: 'none',
                borderColor: '#E37D7A',
                color: '#E37D7A',
                '&:hover': {
                  borderColor: '#D66B68',
                  color: '#D66B68'
                }
              }}
            >
              Remove Link
            </Button>
          </Stack>
        </>
      ) : (
        <Stack sx={{ gap: '8px' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={false}
                onChange={
                  integrationExpired
                    ? handleExpiredIntegrationReconnection
                    : handleEnableVideoConference
                }
                disabled={isLoading || (!hasVideoConferenceIntegration && !integrationExpired)}
                sx={{
                  color: '#5BC4BF',
                  '&.Mui-checked': {
                    color: '#5BC4BF'
                  }
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Enable {getVideoConferenceLabel(provider)}</Typography>
                {!hasVideoConferenceIntegration && !integrationExpired && (
                  <Link
                    href="/admin/settings"
                    target="_blank"
                    sx={{ fontSize: '0.875rem', textDecoration: 'underline' }}
                  >
                    Setup Integration
                  </Link>
                )}
                {integrationExpired && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExpiredIntegrationReconnection}
                    disabled={isLoading}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      padding: '2px 8px',
                      borderColor: '#FFA726',
                      color: '#FFA726',
                      '&:hover': {
                        borderColor: '#FF9800',
                        color: '#FF9800'
                      }
                    }}
                  >
                    Reconnect
                  </Button>
                )}
              </Box>
            }
          />
          {!hasVideoConferenceIntegration && !integrationExpired && (
            <Typography variant="caption" sx={{ color: '#666', ml: 4, display: 'block' }}>
              Video conferencing integration not detected. Please set up your integration to enable
              this feature.
            </Typography>
          )}
          {integrationExpired && (
            <Typography variant="caption" sx={{ color: '#FF9800', ml: 4, display: 'block' }}>
              Your {getVideoConferenceLabel(provider)} integration has expired after 90 days. Click
              &quot;Reconnect&quot; to renew it.
            </Typography>
          )}
          {!integrationExpired && daysUntilExpiry !== null && daysUntilExpiry <= 7 && (
            <Typography variant="caption" sx={{ color: '#FFA726', ml: 4, display: 'block' }}>
              ⚠️ Your {getVideoConferenceLabel(provider)} integration expires in {daysUntilExpiry}{' '}
              day{daysUntilExpiry !== 1 ? 's' : ''}. It will automatically work until then.
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}
